import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  useDeleteCrmTaskTypeMutation,
  useEditCrmTaskTypeMutation,
} from '@app/api/CrmTasks/api';
import {
  DATE_PRESETS,
  type DatePresetId,
} from '@app/components/LeadCard/ui/MessageComposer/components/TaskComposer/data/datePresets';
import type { SelectionPhase } from '@app/components/LeadCard/ui/MessageComposer/components/TaskComposer/hooks/useTaskComposer';
import {
  addMinutesToTime,
  computeChipLabel,
} from '@app/components/LeadCard/ui/MessageComposer/components/TaskComposer/lib/taskDateTime';
import type { TaskExecutor } from '@app/components/LeadCard/ui/MessageComposer/components/TaskComposer/types';
import { COMPLETED, COMPLETED_EXPIRATION } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import {
  crmTasksApi,
  useCreateCrmTaskCommentMutation,
  useCreateCrmTaskTypeMutation,
  useCreateTaskMutation,
  useCompletedCrmTaskMutation,
  useGetCrmTaskTypesQuery,
  useInProgressCrmTaskMutation,
  useLazyGetCrmTaskQuery,
  useSearchCrmTaskUsersQuery,
  useUpdateCrmTaskMutation,
} from '@app/services/rtkQuery/CrmTasks/api';
import type { NCrmTasks } from '@app/services/rtkQuery/CrmTasks/types';
import { useAppDispatch } from '@app/store';

import type { TaskModalSeed } from '../types';
import type { Dayjs } from 'dayjs';

const DEFAULT_TASK_TYPE_ID = 1;

// Completion is keyed off `StatusName`, not the numeric `Status`: the backend
// sends `Status: 3` for `Expired` (просрочена), which is NOT a completed task.
const COMPLETED_STATUS_NAMES = new Set<string>([
  COMPLETED,
  COMPLETED_EXPIRATION,
]);

const parseTaskDateState = (task: NCrmTasks.ModelCrmTask) => {
  const start = dayjs(task.TimeStart);
  const end = dayjs(task.TimeDeadline);

  return {
    startDate: start.startOf('day'),
    endDate: end.startOf('day'),
    startTime: start.format('HH:mm'),
    endTime: end.format('HH:mm'),
    isAllDay: task.IsDeadlineAllDay,
  };
};

const getExecutorName = (owner: NCrmTasks.ModelUserNames): string =>
  [owner.NameLast, owner.NameFirst].filter(Boolean).join(' ') ||
  String(owner.UserId);

interface UseTaskModalFormArgs {
  isOpen: boolean;
  taskId: number | null;
  seed?: TaskModalSeed;
}

export const useTaskModalForm = ({
  isOpen,
  taskId,
  seed,
}: UseTaskModalFormArgs) => {
  const today = useMemo(() => dayjs().startOf('day'), []);
  const isEditMode = taskId !== null;

  const { activeBranchId: ActiveBranchId, userId, user } = useUserPrint();
  const { showNotification } = useNotification();
  const dispatch = useAppDispatch();

  const { data: taskTypes = [] } = useGetCrmTaskTypesQuery(
    { ActiveBranchId },
    { skip: !ActiveBranchId }
  );
  const { data: usersData = [] } = useSearchCrmTaskUsersQuery(
    { searchTerm: '', limit: 0, ActiveBranchId: ActiveBranchId! },
    { skip: !ActiveBranchId }
  );

  const [createCrmTaskTypeMutation] = useCreateCrmTaskTypeMutation();
  const [editCrmTaskTypeMutation] = useEditCrmTaskTypeMutation();
  const [deleteCrmTaskTypeMutation] = useDeleteCrmTaskTypeMutation();
  const [createTask, { isLoading: isCreating }] = useCreateTaskMutation();
  const [updateTask, { isLoading: isUpdating }] = useUpdateCrmTaskMutation();
  const [completeTask, { isLoading: isCompleting }] =
    useCompletedCrmTaskMutation();
  const [reopenTask, { isLoading: isReopening }] =
    useInProgressCrmTaskMutation();
  const [createComment, { isLoading: isCommenting }] =
    useCreateCrmTaskCommentMutation();
  const [fetchTask, { isFetching: isLoadingTask }] = useLazyGetCrmTaskQuery();

  const executors = useMemo<TaskExecutor[]>(
    () =>
      usersData
        .filter((u) => !u.IsDeleted)
        .map((u) => ({
          id: u.UserId,
          name:
            [u.NameLast, u.NameFirst].filter(Boolean).join(' ') ||
            String(u.UserId),
        })),
    [usersData]
  );

  const currentUserExecutor = useMemo<TaskExecutor | null>(() => {
    if (!userId) return null;
    const fallbackName = [user?.nameLast, user?.nameFirst]
      .filter(Boolean)
      .join(' ');
    return { id: userId, name: fallbackName || String(userId) };
  }, [user?.nameFirst, user?.nameLast, userId]);

  // ── Task state ─────────────────────────────────────────────────────────────
  const [task, setTask] = useState<NCrmTasks.ModelCrmTask | null>(null);
  const [description, setDescription] = useState('');
  const [clientId, setClientId] = useState<number | null>(null);
  const [comments, setComments] = useState<NCrmTasks.ModelCrmTaskComment[]>([]);
  const [newComment, setNewComment] = useState('');

  const [selectedTaskType, setSelectedTaskType] =
    useState<NCrmTasks.ModelCrmTaskType | null>(null);
  const [selectedExecutor, setSelectedExecutor] = useState<TaskExecutor | null>(
    null
  );

  // ── Date/time state ────────────────────────────────────────────────────────
  const [startDate, setStartDate] = useState<Dayjs>(today);
  const [endDate, setEndDate] = useState<Dayjs>(today);
  const [preset, setPreset] = useState<DatePresetId | null>('today');
  const [selectionPhase, setSelectionPhase] = useState<SelectionPhase>('idle');
  const [pendingStart, setPendingStart] = useState<Dayjs | null>(null);
  const [hoverDate, setHoverDate] = useState<Dayjs | null>(null);
  const [viewMonth, setViewMonth] = useState<Dayjs>(() =>
    dayjs().startOf('month')
  );
  const [isAllDay, setIsAllDay] = useState(true);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');

  // Seed the form once per opened task (or once per create session). The ref
  // key prevents re-seeding while the modal stays open and the user edits.
  const seededKeyRef = useRef<string | null>(null);

  const resetDateState = useCallback((seedDates?: TaskModalSeed) => {
    const start = seedDates?.timeStart ? dayjs(seedDates.timeStart) : null;
    const end = seedDates?.timeDeadline ? dayjs(seedDates.timeDeadline) : null;

    if (start && end) {
      setStartDate(start.startOf('day'));
      setEndDate(end.startOf('day'));
      setStartTime(start.format('HH:mm'));
      setEndTime(end.format('HH:mm'));
      setIsAllDay(seedDates?.isAllDay ?? false);
      setPreset(null);
      setViewMonth(start.startOf('month'));
    } else {
      const now = dayjs().startOf('day');
      setStartDate(now);
      setEndDate(now);
      setStartTime('09:00');
      setEndTime('10:00');
      setIsAllDay(true);
      setPreset('today');
      setViewMonth(dayjs().startOf('month'));
    }
    setSelectionPhase('idle');
    setPendingStart(null);
    setHoverDate(null);
  }, []);

  const applyTaskToState = useCallback(
    (loadedTask: NCrmTasks.ModelCrmTask) => {
      setTask(loadedTask);
      setDescription(loadedTask.Description ?? '');
      setClientId(loadedTask.Client?.Id ?? null);
      setComments(loadedTask.Comments ?? []);

      const type =
        taskTypes.find(({ Id }) => Id === loadedTask.TaskTypeId) ??
        ({
          Id: loadedTask.TaskTypeId,
          Name: loadedTask.TaskType,
          IconId: loadedTask.TaskTypeIconId,
          Color: loadedTask.TaskTypeColor,
        } as NCrmTasks.ModelCrmTaskType);
      setSelectedTaskType(type);

      const owner = loadedTask.Owners?.[0] ?? null;
      setSelectedExecutor(
        owner ? { id: owner.UserId, name: getExecutorName(owner) } : null
      );

      const dates = parseTaskDateState(loadedTask);
      setStartDate(dates.startDate);
      setEndDate(dates.endDate);
      setStartTime(dates.startTime);
      setEndTime(dates.endTime);
      setIsAllDay(dates.isAllDay);
      setPreset(null);
      setSelectionPhase('idle');
      setPendingStart(null);
      setHoverDate(null);
      setViewMonth(dates.startDate.startOf('month'));
    },
    [taskTypes]
  );

  useEffect(() => {
    if (!isOpen) {
      seededKeyRef.current = null;
      return;
    }

    const key = isEditMode ? `edit-${taskId}` : 'create';
    if (seededKeyRef.current === key) return;
    seededKeyRef.current = key;

    setNewComment('');

    if (!isEditMode) {
      setTask(null);
      setComments([]);
      setDescription('');
      setClientId(seed?.clientId ?? null);
      setSelectedTaskType(null);
      setSelectedExecutor(seed?.executor ?? currentUserExecutor);
      resetDateState(seed);
      return;
    }

    if (!taskId || !ActiveBranchId) return;

    void (async () => {
      try {
        const loaded = await fetchTask({ id: taskId, ActiveBranchId }).unwrap();
        applyTaskToState(loaded);
      } catch (error) {
        showNotification(error);
      }
    })();
  }, [
    isOpen,
    isEditMode,
    taskId,
    ActiveBranchId,
    seed,
    currentUserExecutor,
    resetDateState,
    fetchTask,
    applyTaskToState,
    showNotification,
  ]);

  // Default task type for create mode once types load.
  useEffect(() => {
    if (isEditMode || selectedTaskType !== null || taskTypes.length === 0) {
      return;
    }
    setSelectedTaskType(
      taskTypes.find(({ Id }) => Id === DEFAULT_TASK_TYPE_ID) ??
        taskTypes[0] ??
        null
    );
  }, [isEditMode, taskTypes, selectedTaskType]);

  // ── Task type CRUD (same behaviour as the lead-card composer) ──────────────
  const selectTaskType = useCallback((type: NCrmTasks.ModelCrmTaskType) => {
    setSelectedTaskType(type);
  }, []);

  const createTaskType = useCallback(
    async (params: {
      iconId: string;
      color: string;
      name: string;
    }): Promise<NCrmTasks.ModelCrmTaskType | null> => {
      try {
        const created = await createCrmTaskTypeMutation({
          ActiveBranchId,
          data: {
            Id: 0,
            IconId: params.iconId,
            Color: params.color,
            Name: params.name,
          },
        }).unwrap();
        setSelectedTaskType(created);
        return created;
      } catch {
        return null;
      }
    },
    [ActiveBranchId, createCrmTaskTypeMutation]
  );

  const editTaskType = useCallback(
    async (type: NCrmTasks.ModelCrmTaskType): Promise<void> => {
      if (!ActiveBranchId) return;
      try {
        await editCrmTaskTypeMutation({
          ActiveBranchId,
          data: {
            Id: type.Id,
            IconId: type.IconId ?? null,
            Color: type.Color ?? null,
            Name: type.Name,
          },
        }).unwrap();
        if (selectedTaskType?.Id === type.Id) setSelectedTaskType(type);
        dispatch(crmTasksApi.util.invalidateTags(['CrmTaskTypes']));
      } catch (error) {
        showNotification(error);
      }
    },
    [
      ActiveBranchId,
      dispatch,
      editCrmTaskTypeMutation,
      selectedTaskType?.Id,
      showNotification,
    ]
  );

  const deleteTaskType = useCallback(
    async (id: number): Promise<void> => {
      if (!ActiveBranchId) return;
      try {
        await deleteCrmTaskTypeMutation({ ActiveBranchId, id }).unwrap();
        if (selectedTaskType?.Id === id) {
          const remaining = taskTypes.filter((t) => t.Id !== id);
          setSelectedTaskType(remaining[0] ?? null);
        }
        dispatch(crmTasksApi.util.invalidateTags(['CrmTaskTypes']));
        showNotification('Тип задачи удалён', 'success');
      } catch (error) {
        showNotification(error);
      }
    },
    [
      ActiveBranchId,
      deleteCrmTaskTypeMutation,
      dispatch,
      selectedTaskType?.Id,
      showNotification,
      taskTypes,
    ]
  );

  const selectExecutor = useCallback((executor: TaskExecutor) => {
    setSelectedExecutor(executor);
  }, []);

  // ── Date/time handlers (same behaviour as the lead-card composer) ──────────
  const chipLabel = useMemo(
    () =>
      computeChipLabel(
        startDate,
        endDate,
        preset,
        isAllDay,
        startTime,
        endTime
      ),
    [startDate, endDate, preset, isAllDay, startTime, endTime]
  );

  const previewStart = useMemo((): Dayjs | null => {
    if (selectionPhase !== 'selecting' || !pendingStart) return null;
    if (!hoverDate) return pendingStart;
    return hoverDate.isBefore(pendingStart, 'day') ? hoverDate : pendingStart;
  }, [selectionPhase, pendingStart, hoverDate]);

  const previewEnd = useMemo((): Dayjs | null => {
    if (selectionPhase !== 'selecting' || !pendingStart) return null;
    if (!hoverDate) return pendingStart;
    return hoverDate.isAfter(pendingStart, 'day') ? hoverDate : pendingStart;
  }, [selectionPhase, pendingStart, hoverDate]);

  const selectPreset = useCallback((presetId: DatePresetId) => {
    const found = DATE_PRESETS.find((p) => p.id === presetId);
    if (!found) return;
    const date = found.getDate();
    setStartDate(date);
    setEndDate(date);
    setPreset(presetId);
    setSelectionPhase('idle');
    setPendingStart(null);
    setHoverDate(null);
    setViewMonth(date.startOf('month'));
    setIsAllDay(found.isAllDay);
    if (!found.isAllDay) {
      const st = found.getStartTime();
      const et = found.getEndTime();
      if (st) setStartTime(st);
      if (et) setEndTime(et);
    }
  }, []);

  const handleDayClick = useCallback(
    (day: Dayjs) => {
      if (selectionPhase === 'idle') {
        // A single click already fixes the period to that day; a second click
        // extends it to a range.
        setStartDate(day);
        setEndDate(day);
        setPendingStart(day);
        setHoverDate(day);
        setSelectionPhase('selecting');
        setPreset(null);
      } else if (pendingStart) {
        if (
          day.isSame(pendingStart, 'day') ||
          day.isAfter(pendingStart, 'day')
        ) {
          setStartDate(pendingStart);
          setEndDate(day);
        } else {
          setStartDate(day);
          setEndDate(pendingStart);
        }
        setPendingStart(null);
        setSelectionPhase('idle');
        setHoverDate(null);
      }
    },
    [selectionPhase, pendingStart]
  );

  // Called when the date popup closes: an unfinished range stays a single day.
  const finishDateSelection = useCallback(() => {
    setSelectionPhase('idle');
    setPendingStart(null);
    setHoverDate(null);
  }, []);

  const handleDayHover = useCallback(
    (day: Dayjs | null) => {
      if (selectionPhase === 'selecting') setHoverDate(day);
    },
    [selectionPhase]
  );

  const toggleAllDay = useCallback(() => {
    setIsAllDay((prev) => {
      if (prev) setPreset(null);
      return !prev;
    });
  }, []);

  const handleStartTimeChange = useCallback((time: string) => {
    if (!time) return;
    setStartTime(time);
    setPreset(null);
    const [sh, sm] = time.split(':').map(Number);
    const startTotal = sh * 60 + sm;
    setEndTime((prev) => {
      const [eh, em] = prev.split(':').map(Number);
      if (eh * 60 + em > startTotal) return prev;
      return addMinutesToTime(time, 60);
    });
  }, []);

  const handleEndTimeChange = useCallback((time: string) => {
    if (!time) return;
    setEndTime(time);
    setPreset(null);
  }, []);

  const handleSetDuration = useCallback(
    (minutes: number) => {
      setEndTime(addMinutesToTime(startTime, minutes));
      setPreset(null);
    },
    [startTime]
  );

  // ── Persistence ────────────────────────────────────────────────────────────
  const buildPayload = useCallback((): NCrmTasks.ModelCrmTaskCreate | null => {
    if (!selectedTaskType) {
      showNotification('Выберите тип задачи', 'frontWarning');
      return null;
    }
    const startDateStr = startDate.format('YYYY-MM-DD');
    const endDateStr = endDate.format('YYYY-MM-DD');
    const executorId =
      selectedExecutor === null ? null : Number(selectedExecutor.id);
    return {
      TaskTypeId: selectedTaskType.Id,
      Description: description.trim() || null,
      TimeStart: isAllDay
        ? `${startDateStr}T00:00:00`
        : `${startDateStr}T${startTime}:00`,
      TimeDeadline: isAllDay
        ? `${endDateStr}T23:59:59`
        : `${endDateStr}T${endTime}:00`,
      IsDeadlineAllDay: isAllDay,
      Color: selectedTaskType.Color,
      ClientId: clientId,
      OwnerIds:
        executorId !== null && Number.isFinite(executorId)
          ? [executorId]
          : null,
    };
  }, [
    clientId,
    description,
    endDate,
    endTime,
    isAllDay,
    selectedExecutor,
    selectedTaskType,
    showNotification,
    startDate,
    startTime,
  ]);

  // The comment field has no own button: a typed comment is sent together with
  // the task on «Сохранить». Throws so handleSave keeps the modal open.
  const saveNewComment = useCallback(async (): Promise<void> => {
    const text = newComment.trim();
    if (!text || taskId === null || !ActiveBranchId) return;

    const created = await createComment({
      ActiveBranchId,
      body: { CrmTaskId: taskId, Text: text },
    }).unwrap();
    setComments((prev) => [...prev, created]);
    setNewComment('');
  }, [ActiveBranchId, createComment, newComment, taskId]);

  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!ActiveBranchId) return false;
    const payload = buildPayload();
    if (!payload) return false;

    try {
      if (isEditMode && taskId !== null) {
        await updateTask({
          ActiveBranchId,
          data: { ...payload, Id: taskId },
        }).unwrap();
        await saveNewComment();
        showNotification('Задача обновлена', 'success');
      } else {
        await createTask({
          activeBranchId: ActiveBranchId,
          data: payload,
        }).unwrap();
        showNotification('Задача успешно создана', 'success');
      }
      return true;
    } catch (error) {
      showNotification(error);
      return false;
    }
  }, [
    ActiveBranchId,
    buildPayload,
    createTask,
    isEditMode,
    saveNewComment,
    showNotification,
    taskId,
    updateTask,
  ]);

  const isCompleted = useMemo(
    () => (task ? COMPLETED_STATUS_NAMES.has(task.StatusName ?? '') : false),
    [task]
  );

  const handleToggleComplete = useCallback(async (): Promise<boolean> => {
    if (taskId === null || !ActiveBranchId) return false;
    try {
      if (isCompleted) {
        await reopenTask({ id: taskId, ActiveBranchId }).unwrap();
        showNotification('Задача возвращена в работу', 'success');
      } else {
        await completeTask({ id: taskId, ActiveBranchId }).unwrap();
        showNotification('Задача выполнена', 'success');
      }

      // Re-read the task rather than patching it locally: completion also moves
      // `StatusName` (Expired -> CompletedExpiration), and the mutation
      // responses do not carry the full task shape.
      setTask(await fetchTask({ id: taskId, ActiveBranchId }).unwrap());

      return true;
    } catch (error) {
      showNotification(error);
      return false;
    }
  }, [
    ActiveBranchId,
    completeTask,
    fetchTask,
    isCompleted,
    reopenTask,
    showNotification,
    taskId,
  ]);

  return {
    isEditMode,
    isLoadingTask,
    isSaving: isCreating || isUpdating || isCommenting,
    isCompleting: isCompleting || isReopening,
    task,
    isCompleted,
    // description + client
    description,
    setDescription,
    clientId,
    // comments
    comments,
    newComment,
    setNewComment,
    // task types
    taskTypes,
    selectedTaskType,
    selectTaskType,
    createTaskType,
    editTaskType,
    deleteTaskType,
    taskTypeLabel: selectedTaskType?.Name ?? 'Тип задачи',
    taskTypeIconId: selectedTaskType?.IconId ?? null,
    taskTypeColor: selectedTaskType?.Color ?? null,
    isTaskTypeMuted: selectedTaskType === null,
    // executor
    executors,
    selectedExecutor,
    selectExecutor,
    responsibleLabel: selectedExecutor?.name ?? 'Не выбран',
    isResponsibleMuted: selectedExecutor === null,
    // date/time
    chipLabel,
    startDate,
    endDate,
    preset,
    selectionPhase,
    previewStart,
    previewEnd,
    viewMonth,
    isAllDay,
    startTime,
    endTime,
    selectPreset,
    handleDayClick,
    handleDayHover,
    finishDateSelection,
    setViewMonth,
    toggleAllDay,
    handleStartTimeChange,
    handleEndTimeChange,
    handleSetDuration,
    // persistence
    handleSave,
    handleToggleComplete,
  };
};
